import React from 'react';
import sealUrl from '../assets/ttb-seal.png';
import './TtbForm51309.css';

type Cols = {
  cellar: number;
  rackingBulk: number;
  rackingKeg: number;
  bottlingBulk: number;
  bottlingCase: number;
  totals: number;
};

export type TtbFormReport = {
  omb?: string;
  header: {
    breweryName: string;
    ein: string;
    ttbNumber: string;
    street: string;
    city: string;
    county?: string;
    state: string;
    zip: string;
    phone: string;
  };
  period: {
    year: number;
    quarter: number;
    label?: string;
  };
  lines: {
    line: number;
    cellar?: number;
    rackingBulk?: number;
    rackingKeg?: number;
    bottlingBulk?: number;
    bottlingCase?: number;
    totals?: number;
    cols?: Cols;
  }[];
  remarks?: {
    text?: string;
    generatedText?: string;
  } | null;
};

const COL_KEYS = [
  'cellar',
  'rackingBulk',
  'rackingKeg',
  'bottlingBulk',
  'bottlingCase',
  'totals',
] as const;

/** Cells the paper form grays out. Totals are never blocked. Keep in sync with server/services/ttb-form-5130-9-pdf.js */
const BLOCKED: Record<number, string[]> = {
  2: ['rackingBulk', 'rackingKeg', 'bottlingBulk', 'bottlingCase'],
  3: ['rackingBulk', 'rackingKeg', 'bottlingBulk', 'bottlingCase'],
  4: ['rackingBulk', 'rackingKeg', 'bottlingBulk', 'bottlingCase'],
  5: ['rackingBulk', 'bottlingBulk'],
  6: ['cellar', 'rackingKeg', 'bottlingCase'],
  7: ['rackingBulk', 'bottlingBulk'],
  8: ['rackingBulk', 'bottlingBulk'],
  9: ['cellar', 'rackingBulk', 'bottlingBulk', 'bottlingCase'],
  10: ['cellar', 'rackingBulk', 'rackingKeg', 'bottlingBulk'],
  14: ['cellar', 'rackingBulk', 'bottlingBulk'],
  15: ['rackingBulk', 'bottlingBulk'],
  16: ['rackingBulk', 'bottlingBulk'],
  17: ['cellar', 'rackingBulk', 'bottlingBulk'],
  18: ['rackingBulk', 'bottlingBulk'],
  19: ['rackingBulk', 'bottlingBulk'],
  20: ['rackingBulk', 'rackingKeg', 'bottlingBulk', 'bottlingCase'],
  21: ['rackingBulk', 'bottlingBulk'],
  22: ['rackingBulk', 'rackingKeg', 'bottlingBulk', 'bottlingCase'],
  23: ['rackingBulk', 'rackingKeg', 'bottlingBulk', 'bottlingCase'],
  24: ['cellar', 'rackingKeg', 'bottlingCase'],
  25: ['cellar', 'rackingKeg', 'bottlingBulk', 'bottlingCase'],
  26: ['cellar', 'rackingBulk', 'rackingKeg', 'bottlingCase'],
};

const ALWAYS_SHOW = new Set([1, 10, 13, 23, 26, 33, 34]);

const LINE_TEXT: Record<number, string> = {
  1: 'On hand beginning of this report period (see line 33 totals from last monthly report or line 17 from last quarterly report)',
  2: 'Beer produced by fermentation',
  3: 'Addition of water and other liquids',
  4: 'Beer received from racking and bottling',
  5: 'Beer received in bond (see Instruction #10)',
  6: 'Beer received from cellars',
  7: 'Beer returned to this brewery after removal from this brewery (see Instruction #11)',
  8: 'Beer returned to the brewery after removal from another brewery of same ownership (see Instruction #11)',
  9: 'Racked',
  10: 'Bottled',
  11: 'Physical inventory disclosed an overage',
  12: '',
  13: 'Total additions to inventory, plus beer on hand (add all columns in lines 1 through 12)',
  14: 'Removed for consumption or sale (see Instruction #7)',
  15: 'Removed tax-determined for consumption or sale to tavern on brewery premises (see Instruction #12)',
  16: 'Removed without payment of tax for export',
  17: 'Removed without payment of tax for use as supplies (vessels/aircraft)',
  18: 'Removed without payment of tax for use in research and development',
  19: 'Removed without payment of tax to other breweries and pilot brewing plants of same ownership',
  20: 'Removed without payment of tax as beer unfit for sale removed for use in manufacturing',
  21: 'Beer consumed on premises (see Instruction #12)',
  22: 'Beer transferred for racking',
  23: 'Beer transferred for bottling',
  24: 'Beer returned to cellars',
  25: 'Beer racked',
  26: 'Beer bottled',
  27: 'Laboratory samples',
  28: 'Beer destroyed at brewery (see Instruction #13)',
  29: 'Beer transferred to a distilled spirits plant',
  30: 'Losses, including theft (see Instruction #14)',
  31: 'Physical inventory disclosed a shortage (see Instruction #14)',
  32: '',
  33: 'Total amount of beer on hand at the end of this period (see Instruction #15)',
  34: 'Total beer (see Instruction #16)',
};

const QUARTERS = [
  'January - March',
  'April - June',
  'July - September',
  'October - December',
];

export function lineValues(line: TtbFormReport['lines'][number] | undefined): Cols {
  const src = line?.cols || line;
  const n = (v: number | undefined) => Math.round((Number(v) || 0) * 100) / 100;
  return {
    cellar: n(src?.cellar),
    rackingBulk: n(src?.rackingBulk),
    rackingKeg: n(src?.rackingKeg),
    bottlingBulk: n(src?.bottlingBulk),
    bottlingCase: n(src?.bottlingCase),
    totals: n(src?.totals),
  };
}

export function cellText(lineNo: number, key: (typeof COL_KEYS)[number], cols: Cols): string | null {
  if ((BLOCKED[lineNo] || []).includes(key)) return null;
  const any = COL_KEYS.some((k) => cols[k] !== 0);
  if (!any && !ALWAYS_SHOW.has(lineNo)) return '';
  return cols[key].toFixed(2);
}

function splitPhone(phone: string): { area: string; rest: string } {
  const m = String(phone || '').match(/(\d{3})\D*(\d{3}\D?\d{4})/);
  if (!m) return { area: '', rest: phone || '' };
  const digits = m[2].replace(/\D/g, '');
  const rest = digits.length === 7 ? `${digits.slice(0, 3)}-${digits.slice(3)}` : m[2];
  return { area: m[1], rest };
}

function Footer({ page }: { page: number }) {
  return (
    <div className="ttb-f5130__footer">
      <div className="ttb-f5130__page-no">Page {page} of 2</div>
      <div>TTB F 5130.9 (04/2015)</div>
      <small>See instructions and Paperwork Reduction Act Notice on TTB F 5130.9i.</small>
    </div>
  );
}

const TtbForm51309Sheet: React.FC<{ report: TtbFormReport }> = ({ report }) => {
  const h = report.header;
  const phone = splitPhone(h.phone);
  const by = Object.fromEntries(report.lines.map((l) => [l.line, l]));
  const quarter = Number(report.period.quarter);

  const dataRow = (n: number) => {
    const cols = lineValues(by[n]);
    const total = n === 13 || n === 33 || n === 34;
    return (
      <tr key={n} className={total ? 'ttb-f5130__total' : undefined}>
        <td className="ttb-f5130__ops">
          {n}. {LINE_TEXT[n]}
        </td>
        {COL_KEYS.map((key) => {
          const blocked = (BLOCKED[n] || []).includes(key);
          const text = cellText(n, key, cols);
          return (
            <td
              key={key}
              className={blocked ? 'ttb-f5130__num ttb-f5130__blocked' : 'ttb-f5130__num'}
              data-testid={`ttb-cell-${n}-${key}`}
            >
              {text || ''}
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div className="ttb-f5130" data-testid="ttb-f5130">
      <article className="ttb-f5130__page" aria-label="TTB F 5130.9 page 1">
        <div className="ttb-f5130__omb">OMB No. {report.omb || '1513-0007'}</div>
        <header className="ttb-f5130__top">
          <img className="ttb-f5130__seal" src={sealUrl} alt="Alcohol and Tobacco Tax and Trade Bureau" />
          <div className="ttb-f5130__titles">
            <div className="ttb-f5130__dept">DEPARTMENT OF THE TREASURY</div>
            <div className="ttb-f5130__bureau">ALCOHOL AND TOBACCO TAX AND TRADE BUREAU (TTB)</div>
            <div className="ttb-f5130__name">BREWER&apos;S REPORT OF OPERATIONS</div>
          </div>
          <div className="ttb-f5130__id">
            <div className="ttb-f5130__id-row">
              <span>Our Brewery EIN is:</span>
              <strong>{h.ein}</strong>
            </div>
            <div className="ttb-f5130__id-row">
              <span>Our TTB Brewery Number is:</span>
              <strong>{h.ttbNumber}</strong>
            </div>
            <div className="ttb-f5130__id-row">
              <span>TTB can reach the brewery by calling:</span>
              <strong>
                ( {phone.area} ) {phone.rest}
              </strong>
            </div>
          </div>
        </header>

        <p className="ttb-f5130__field-label">What is the name of your brewery?</p>
        <div className="ttb-f5130__value">{h.breweryName}</div>
        <p className="ttb-f5130__field-label">What is the location of your brewery?</p>
        <div className="ttb-f5130__addr">
          <div>{h.street}</div>
          <div>{h.city}</div>
          <div>{h.county || ''}</div>
          <div>{h.state}</div>
          <div>{h.zip}</div>
        </div>
        <div className="ttb-f5130__captions">
          <span>(Number &amp; Street)</span>
          <span>(City)</span>
          <span>(County)</span>
          <span>(State)</span>
          <span>(ZIP Code)</span>
        </div>

        <div className="ttb-f5130__period">
          <div>
            <div className="ttb-f5130__year">
              <span>Reporting Period (enter year)</span>
              <strong>{report.period.year}</strong>
            </div>
            <div className="ttb-f5130__month">
              <span>Monthly Report for (enter month)</span>
              <i />
              <span className="ttb-f5130__or">OR</span>
            </div>
          </div>
          <div>
            <div>Quarterly Report for:</div>
            <div className="ttb-f5130__quarters">
              {QUARTERS.map((label, i) => {
                const on = quarter === i + 1;
                return (
                  <label key={label} className="ttb-f5130__q">
                    <span
                      className="ttb-f5130__box"
                      data-testid={`ttb-quarter-${i + 1}`}
                      data-checked={on ? 'true' : 'false'}
                    >
                      {on ? '✓' : ''}
                    </span>
                    {label}
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <div className="ttb-f5130__part">Part 1 - Beer Summary</div>
        <table className="ttb-f5130__grid">
          <colgroup>
            <col style={{ width: '40%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead>
            <tr>
              <th rowSpan={2}>
                Operations
                <div>(a)</div>
              </th>
              <th rowSpan={2}>
                Cellar
                <div>(b)</div>
              </th>
              <th colSpan={2}>Racking</th>
              <th colSpan={2}>Bottling</th>
              <th rowSpan={2}>
                Totals
                <div>(g)</div>
              </th>
            </tr>
            <tr>
              <th>
                Bulk
                <div>(c)</div>
              </th>
              <th>
                Keg
                <div>(d)</div>
              </th>
              <th>
                Bulk
                <div>(e)</div>
              </th>
              <th>
                Case
                <div>(f)</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="ttb-f5130__banner">
              <td colSpan={7}>
                Additions to Beer Inventory (round your entries to the nearest second decimal)
              </td>
            </tr>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].map(dataRow)}
            <tr className="ttb-f5130__banner">
              <td colSpan={7}>
                Removals from Beer Inventory (round your entries to the nearest second decimal)
              </td>
            </tr>
            {[14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34].map(
              dataRow
            )}
          </tbody>
        </table>
        <Footer page={1} />
      </article>

      <article className="ttb-f5130__page" aria-label="TTB F 5130.9 page 2">
        <div className="ttb-f5130__p2-title">Prior Period Adjustments (see instruction #16)</div>
        <div className="ttb-f5130__adjust">
          <div>
            35. Additions to beer inventory <span>(+)</span>
            <i className="ttb-f5130__blank" />
            <span>(–)</span>
            <i className="ttb-f5130__blank" />
          </div>
          <div>
            36. Removals from beer inventory <span>(+)</span>
            <i className="ttb-f5130__blank" />
            <span>(–)</span>
            <i className="ttb-f5130__blank" />
          </div>
        </div>
        <p className="ttb-f5130__perjury">
          Under penalties of perjury I declare that this report is supported by true, complete, and
          correct records that are available for inspection at my brewery. I have examined this
          report and to the best of my knowledge and belief it is true, complete, and correct.
        </p>
        <div className="ttb-f5130__sign">
          <span>Signature</span>
          <span>Title</span>
          <span>Date</span>
        </div>
        <div className="ttb-f5130__part">Part 2 - Cereal Beverage Summary</div>
        <div className="ttb-f5130__sub">(products that are less than 0.5% alcohol by volume)</div>
        <table className="ttb-f5130__cereal">
          <tbody>
            <tr>
              <td>
                1. Produced <i className="ttb-f5130__blank" /> Bbls.
              </td>
              <td>
                4. Loss and wastage <i className="ttb-f5130__blank" /> Bbls.
              </td>
            </tr>
            <tr>
              <td>
                2. Removed <i className="ttb-f5130__blank" /> Bbls.
              </td>
              <td>
                5. <i className="ttb-f5130__blank" /> Bbls.
              </td>
            </tr>
            <tr>
              <td>
                3. Received <i className="ttb-f5130__blank" /> Bbls.
              </td>
              <td>
                6. Total on hand end of period <i className="ttb-f5130__blank" /> Bbls.
              </td>
            </tr>
          </tbody>
        </table>
        <div className="ttb-f5130__part">Part 3 – Remarks</div>
        <div className="ttb-f5130__sub">
          (Add remarks below or on a separate piece of paper attached to this form)
        </div>
        <div className="ttb-f5130__remarks" data-testid="ttb-form-remarks">
          {(report.remarks?.text || report.remarks?.generatedText || '')
            .split('\n')
            .filter(Boolean)
            .map((line, i) => (
              <div key={i} className="ttb-f5130__remarks-line">
                {line}
              </div>
            ))}
        </div>
        <Footer page={2} />
      </article>
    </div>
  );
};

export default TtbForm51309Sheet;
